import React, { useState } from 'react'
import styled from 'styled-components'
import { Sun, Moon, Monitor } from 'lucide-react'
import { useTheme } from '../../contexts/ThemeContext'
import { ThemeMode } from '../../theme/theme'

const SwitcherContainer = styled.div`
  position: relative;
`

const SwitcherButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: ${({ theme }) => theme.spacing.xs};
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transform: ${(props) => (props.$isOpen ? 'scale(1)' : 'scale(0.95)')};
  transition: ${({ theme }) => theme.transitions.default};
  z-index: 1000;
  min-width: 140px;
`

const MenuItem = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.md};
  background: ${(props) => (props.$isActive ? props.theme.colors.primary + '20' : 'none')};
  border: none;
  color: ${(props) => (props.$isActive ? props.theme.colors.primary : props.theme.colors.text)};
  font-size: ${({ theme }) => theme.typography.sm};
  text-align: left;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  
  &:hover {
    background-color: ${(props) => (props.$isActive ? props.theme.colors.primary + '30' : props.theme.colors.surfaceHover)};
  }
  
  &:first-child {
    border-radius: ${({ theme }) => theme.borderRadius.sm} ${({ theme }) => theme.borderRadius.sm} 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 ${({ theme }) => theme.borderRadius.sm} ${({ theme }) => theme.borderRadius.sm};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`

const themeOptions: { mode: ThemeMode; label: string; icon: React.ComponentType }[] = [
  { mode: 'light', label: 'Light', icon: Sun },
  { mode: 'dark', label: 'Dark', icon: Moon },
  { mode: 'system', label: 'System', icon: Monitor },
]

export const ThemeSwitcher: React.FC = () => {
  const { themeMode, setThemeMode } = useTheme()
  const [isOpen, setIsOpen] = useState(false)

  const currentOption = themeOptions.find((option) => option.mode === themeMode)
  const CurrentIcon = currentOption?.icon || Monitor

  const handleThemeChange = (mode: ThemeMode) => {
    setThemeMode(mode)
    setIsOpen(false)
  }

  const toggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsOpen(!isOpen)
  }

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => setIsOpen(false)
    if (isOpen) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [isOpen])

  return (
    <SwitcherContainer>
      <SwitcherButton onClick={toggleDropdown} title={`Current theme: ${currentOption?.label || 'System'}`}>
        <CurrentIcon />
      </SwitcherButton>

      <DropdownMenu $isOpen={isOpen} onClick={(e) => e.stopPropagation()}>
        {themeOptions.map(({ mode, label, icon: Icon }) => (
          <MenuItem key={mode} $isActive={mode === themeMode} onClick={() => handleThemeChange(mode)}>
            <Icon />
            {label}
          </MenuItem>
        ))}
      </DropdownMenu>
    </SwitcherContainer>
  )
}
